/**
 * Create a notification channel — provider card picker → typed config form → submit.
 * Multi-type option leaves `type` empty (null on submit).
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Bell, Layers } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Stepper,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useCreateChannel, type ChannelType } from '@/api/healthcheck';
import {
  PROVIDER_META,
  PROVIDER_TYPES,
} from './channel-provider-meta';
import {
  ChannelProviderConfigForm,
  isProviderConfigValid,
  buildConfigPayload,
  type ProviderConfigState,
} from './ChannelProviderConfigForms';
import { TeamsModeToggle, type TeamsMode } from './TeamsModeToggle';
import styles from './ChannelCreate.module.css';

const STEPS = ['Provider', 'Configure', 'Review'];

export function ChannelCreate() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;

  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [selectedType, setSelectedType] = useState<ChannelType | 'multi' | null>(null);
  const [name, setName] = useState('');
  const [config, setConfig] = useState<ProviderConfigState>({});
  // Teams only: webhook (collect URL now) vs oauth2 (connect M365 after create).
  const [teamsMode, setTeamsMode] = useState<TeamsMode>('webhook');
  const [showErrors, setShowErrors] = useState(false);

  const isTeamsOAuth = selectedType === 'teams' && teamsMode === 'oauth2';

  const create = useCreateChannel({
    onSuccess: (ch) => navigate(`${base}/notifications/${ch.id}`),
  });

  const goto = (i: number) => {
    setStep(i);
    setReached((r) => Math.max(r, i));
  };

  const nameOk = name.trim().length > 0 && name.trim().length <= 255;
  const configOk =
    selectedType === 'multi' ||
    // OAuth Teams needs no field config — the M365 connection happens post-create.
    isTeamsOAuth ||
    (selectedType !== null && isProviderConfigValid(selectedType, config));

  const next = () => {
    if (step === 0 && !selectedType) return;
    if (step === 1) {
      if (!nameOk || !configOk) {
        setShowErrors(true);
        return;
      }
    }
    setShowErrors(false);
    goto(step + 1);
  };

  const submit = () => {
    if (!selectedType || !nameOk || !configOk) {
      setShowErrors(true);
      setStep(1);
      return;
    }
    const type = selectedType === 'multi' ? null : selectedType;
    // Teams OAuth: persist mode so ChannelDetail shows the M365 connect panel.
    // Other typed channels: nest the built field config under the provider key.
    const configPayload = isTeamsOAuth
      ? { teams: { mode: 'oauth2' } }
      : type !== null
        ? { [type]: buildConfigPayload(type, config) }
        : {};

    create.mutate({ name: name.trim(), type, config: configPayload });
  };

  const summary = (
    <Card>
      <CardHeader>
        <CardTitle>Summary</CardTitle>
        <CardDescription>Review before creating.</CardDescription>
      </CardHeader>
      <CardBody>
        <dl style={{ display: 'grid', gap: 'var(--vn-space-2)', margin: 0 }}>
          <SummaryRow
            label="Provider"
            value={
              selectedType === 'multi'
                ? 'Multi-type'
                : selectedType
                  ? PROVIDER_META[selectedType].label
                  : '—'
            }
          />
          <SummaryRow label="Name" value={name || '—'} />
          {selectedType === 'teams' && (
            <SummaryRow
              label="Delivery mode"
              value={teamsMode === 'oauth2' ? 'Microsoft 365 (OAuth)' : 'Webhook URL'}
            />
          )}
        </dl>
      </CardBody>
    </Card>
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Bell size={20} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={`${base}/notifications`}>Channels</Link></BreadcrumbItem>
            <BreadcrumbItem current>New channel</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="New notification channel"
        description="Choose a provider and configure the delivery destination."
      />

      <div style={{ marginBottom: 'var(--vn-space-5)' }}>
        <Stepper steps={STEPS} current={step} reached={reached} onStepClick={goto} />
      </div>

      <div className={styles.twoCol}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 640 }}>
          {/* Step 0 — Provider picker */}
          {step === 0 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Choose a provider</CardTitle>
                <CardDescription>Select the delivery type for this channel.</CardDescription>
              </CardHeader>
              <CardBody>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 'var(--vn-space-3)' }}>
                  {PROVIDER_TYPES.map((t) => {
                    const meta = PROVIDER_META[t];
                    const Icon = meta.icon;
                    const active = selectedType === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => { setSelectedType(t); setConfig({}); setTeamsMode('webhook'); }}
                        style={providerCardStyle(active)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 4 }}>
                          <Icon size={17} />
                          <span style={{ fontWeight: 600 }}>{meta.label}</span>
                        </div>
                        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                          {meta.description}
                        </div>
                      </button>
                    );
                  })}

                  {/* Multi-type option */}
                  {(() => {
                    const active = selectedType === 'multi';
                    return (
                      <button
                        type="button"
                        onClick={() => { setSelectedType('multi'); setConfig({}); setTeamsMode('webhook'); }}
                        style={providerCardStyle(active)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 4 }}>
                          <Layers size={17} />
                          <span style={{ fontWeight: 600 }}>Multi-type</span>
                        </div>
                        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                          One channel, multiple providers. Config nested by provider key.
                        </div>
                      </button>
                    );
                  })()}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Step 1 — Name + config */}
          {step === 1 && selectedType && (
            <>
              <Card flat>
                <CardHeader>
                  <CardTitle>Channel details</CardTitle>
                </CardHeader>
                <CardBody>
                  <Field
                    label="Name"
                    required
                    error={showErrors && !nameOk ? 'Name is required (max 255 characters).' : undefined}
                  >
                    {(p) => (
                      <Input
                        {...p}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Ops Telegram"
                        maxLength={255}
                        style={{ maxWidth: 420 }}
                        invalid={showErrors && !nameOk}
                      />
                    )}
                  </Field>
                </CardBody>
              </Card>

              {selectedType !== 'multi' && (
                <Card flat>
                  <CardHeader>
                    <CardTitle>{PROVIDER_META[selectedType].label} configuration</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                      {/* Teams: pick delivery mode up front (shared toggle). */}
                      {selectedType === 'teams' && (
                        <TeamsModeToggle value={teamsMode} onChange={setTeamsMode} />
                      )}

                      {isTeamsOAuth ? (
                        <Alert tone="info" title="Microsoft 365 (OAuth)">
                          You&apos;ll connect Microsoft 365 right after creating this channel —
                          we&apos;ll take you to the channel page to authorize and pick a team.
                        </Alert>
                      ) : (
                        <ChannelProviderConfigForm
                          type={selectedType}
                          config={config}
                          onChange={(patch) => setConfig((c) => ({ ...c, ...patch }))}
                          showErrors={showErrors}
                          isEditing={false}
                        />
                      )}
                    </div>
                  </CardBody>
                </Card>
              )}

              {selectedType === 'multi' && (
                <Card flat>
                  <CardBody>
                    <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                      Multi-type channels hold configs for multiple providers nested under each provider
                      key (e.g. <code>telegram</code>, <code>email</code>). Configure each provider's
                      config via the API or edit the channel after creation.
                    </p>
                  </CardBody>
                </Card>
              )}

              {create.error && (
                <Alert tone="danger" title={(create.error as Error).message || 'Create failed'} />
              )}
            </>
          )}

          {/* Step 2 — Confirm */}
          {step === 2 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Ready to create</CardTitle>
                <CardDescription>Confirm the summary on the right, then create the channel.</CardDescription>
              </CardHeader>
              <CardBody>
                <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>
                  After creating, add subscribers to the channel before sending a test notification.
                </p>
              </CardBody>
            </Card>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
            <Button
              variant="ghost"
              onClick={() =>
                step === 0 ? navigate(`${base}/notifications`) : goto(step - 1)
              }
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            {step < 2 ? (
              <Button onClick={next} disabled={step === 0 && !selectedType}>
                Continue
              </Button>
            ) : (
              <Button onClick={submit} disabled={create.isPending}>
                Create channel
              </Button>
            )}
          </div>
        </div>

        <div>{summary}</div>
      </div>
    </Container>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)' }}>
      <dt style={{ color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd style={{ margin: 0, textAlign: 'right', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</dd>
    </div>
  );
}

function providerCardStyle(active: boolean): React.CSSProperties {
  return {
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    color: 'var(--vn-fg-default)',
    border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
    outline: active ? '1px solid var(--vn-primary-solid)' : 'none',
    borderRadius: 'var(--vn-radius-lg)',
    background: active ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
    padding: 'var(--vn-space-3)',
  };
}

